import { fm } from '../dates';
import { bin } from '../format';
import { DASH, HATCH, RAMP_FG, hatchOf } from '../palette';

/** "C3" + the cycle's start, under each trend bar. */
export const cycleLabel = (c) => ({ label: `C${c.n}`, sub: fm(c.start) });

/** Heatmap column header for a cycle. */
export const cycleColumn = (c) => `C${c.n}${c.completed ? '' : ' · to date'}`;

/**
 * Trend chart model (Wellness Trend's `trend` prop): bar heights, tick
 * positions and the dashed average line, as percentages of [lo, hi].
 */
export function bars(items, lo, hi, color, avg, avgLabel, ticks) {
  const pct = (v) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  return {
    ticks: ticks.map((tk) => ({ b: `${pct(tk)}%`, label: String(tk) })),
    avgB: avg == null ? '0%' : `${pct(avg)}%`,
    avgOp: avg == null ? '0' : '1',
    avgTxt: avgLabel,
    bars: items.map((it) => {
      const h = it.v == null ? 0 : pct(it.v);
      return {
        label: it.label,
        sub: it.sub,
        vTxt: it.v == null ? '' : it.txt,
        h: `${h}%`,
        labB: `calc(${h}% + 4px)`,
        bg: it.v == null ? 'transparent' : it.ongoing ? hatchOf(color) : color,
        border: it.ongoing && it.v != null ? `1.5px dashed ${color}` : 'none',
        emptyTxt: it.v == null ? it.empty || 'No data' : '',
      };
    }),
  };
}

/** Legend: completed / ongoing bars and the average line. */
export const cycleLegend = (color, rl, { ongoing = true } = {}) =>
  [
    { w: '12px', h: '12px', bg: color, border: 'none', label: 'Completed cycle' },
    ongoing && { w: '12px', h: '12px', bg: hatchOf(color), border: `1.5px dashed ${color}`, label: 'Ongoing · cycle to date' },
    { w: '18px', h: '2px', bg: DASH, border: 'none', label: `Average · ${rl.toLowerCase()}` },
  ].filter(Boolean);

/** One heatmap / period-day cell: "—" missing, outlined 0 recorded zero, ramp otherwise. */
export function heatCell(v, max, ramp, fmt) {
  if (v == null) return { txt: '—', bg: HATCH, fg: '#8a8a8a', ring: 'inset 0 0 0 1px #E8E0D9', tip: 'No data' };
  if (v === 0) return { txt: '0', bg: '#fff', fg: '#1f1f1f', ring: 'inset 0 0 0 1px #CFC4BB', tip: 'Recorded 0' };
  const i = bin(v, max);
  return { txt: fmt(v), bg: ramp[i], fg: RAMP_FG[i], ring: 'none', tip: fmt(v) };
}

export const NA_CELL = { txt: 'n/a', bg: '#FBFAF9', fg: '#8a8a8a', ring: 'inset 0 0 0 1px #F1ECE8', tip: 'Phase unavailable — cycle ongoing' };

/** A collapsible detail section. `defaultOpen` is the design's initial state. */
export const detailSection = (id, title, desc, body, defaultOpen) => ({ id, title, desc, defaultOpen, ...body });
